import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  fetchBotEncuestaLatest,
  fetchBotEncuestaList,
  fetchBotEncuestaPublic,
  saveBotEncuesta,
  submitBotEncuestaResponse
} from '../api/botEncuesta';
import BotEncuestaSurveyList from '../components/botEncuesta/BotEncuestaSurveyList';
import { useAuth } from '../context/AuthContext';
import '../styles/bot-encuesta-premium.css';
import '../styles/bot-encuesta-survey-list.css';

function hasPermission(user, code) {
  return Boolean((user?.permissions || []).includes(code));
}

const QUESTION_TYPES = [
  { id: 'rating', label: 'Valoración 1–5', icon: '★' },
  { id: 'si_no', label: 'Sí / No', icon: '✓' },
  { id: 'texto', label: 'Texto libre', icon: '✎' },
  { id: 'opciones', label: 'Opciones', icon: '☰' }
];

function emptyQuestion(orden) {
  return {
    orden,
    tipo: 'rating',
    titulo: '',
    opciones: [''],
    requerida: true
  };
}

function normalizeLoadedQuestions(list) {
  if (!Array.isArray(list) || !list.length) {
    return [emptyQuestion(1), emptyQuestion(2)];
  }
  return list.map((q, index) => ({
    orden: q.orden || index + 1,
    tipo: q.tipo || 'texto',
    titulo: q.titulo || '',
    opciones: Array.isArray(q.opciones) && q.opciones.length ? q.opciones : [''],
    requerida: q.requerida !== false
  }));
}

function blankNewSurveyState() {
  return {
    surveyId: null,
    publicToken: '',
    estado: 'borrador',
    titulo: '',
    descripcion: '',
    mensajeGracias: '',
    preguntas: [emptyQuestion(1)]
  };
}

function applySurveySource(source, { data = null } = {}) {
  const row = data || source;
  return {
    surveyId: row?.id || null,
    publicToken: row?.public_token || '',
    estado: row?.estado || 'borrador',
    titulo: source?.titulo || '',
    descripcion: source?.descripcion || '',
    mensajeGracias: source?.mensaje_gracias || '',
    preguntas: normalizeLoadedQuestions(source?.preguntas)
  };
}

function BotEncuestaBuilder() {
  const { user } = useAuth();
  const canSeeReview = hasPermission(user, 'ver_encuesta_bot_review');
  const [initialLoading, setInitialLoading] = useState(true);
  const [switchingSurvey, setSwitchingSurvey] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [surveyList, setSurveyList] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [isNewDraft, setIsNewDraft] = useState(false);
  const [surveyId, setSurveyId] = useState(null);
  const [publicToken, setPublicToken] = useState('');
  const [estado, setEstado] = useState('borrador');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [mensajeGracias, setMensajeGracias] = useState('');
  const [preguntas, setPreguntas] = useState([]);
  const blockAutoLoadRef = useRef(false);

  const applyBlankForm = useCallback(() => {
    const next = blankNewSurveyState();
    setSurveyId(next.surveyId);
    setPublicToken(next.publicToken);
    setEstado(next.estado);
    setTitulo(next.titulo);
    setDescripcion(next.descripcion);
    setMensajeGracias(next.mensajeGracias);
    setPreguntas(next.preguntas);
  }, []);

  const applyToForm = useCallback((source, opts = {}) => {
    const next = applySurveySource(source, opts);
    setSurveyId(next.surveyId);
    setPublicToken(next.publicToken);
    setEstado(next.estado);
    setTitulo(next.titulo);
    setDescripcion(next.descripcion);
    setMensajeGracias(next.mensajeGracias);
    setPreguntas(next.preguntas);
  }, []);

  const refreshSurveyList = useCallback(async () => {
    const json = await fetchBotEncuestaList();
    const list = Array.isArray(json?.data) ? json.data : [];
    setSurveyList(list);
    return list;
  }, []);

  const loadSurveyById = useCallback(async (id) => {
    setSwitchingSurvey(true);
    setError('');
    try {
      const json = await fetchBotEncuestaLatest(id);
      if (blockAutoLoadRef.current) return;
      if (json?.data) {
        applyToForm(json.data, { data: json.data });
        setSelectedListId(json.data.id);
        setIsNewDraft(false);
      }
    } catch (err) {
      if (!blockAutoLoadRef.current) {
        setError(err.message || 'No se pudo cargar la encuesta');
      }
    } finally {
      setSwitchingSurvey(false);
    }
  }, [applyToForm]);

  const startNewSurvey = useCallback(() => {
    blockAutoLoadRef.current = true;
    setError('');
    setSuccess('');
    setSelectedListId(null);
    setIsNewDraft(true);
    setSwitchingSurvey(false);
    applyBlankForm();
  }, [applyBlankForm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setListLoading(true);
        const list = await refreshSurveyList();
        if (cancelled || blockAutoLoadRef.current) return;
        if (list.length) {
          await loadSurveyById(list[0].id);
        } else {
          blockAutoLoadRef.current = true;
          applyBlankForm();
          setIsNewDraft(true);
        }
      } catch (err) {
        if (!cancelled && !blockAutoLoadRef.current) {
          setError(err.message || 'No se pudo cargar las encuestas');
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
          setInitialLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [applyBlankForm, loadSurveyById, refreshSurveyList]);

  const handleSelectSurvey = async (id) => {
    if (Number(selectedListId) === Number(id) && !isNewDraft) return;
    blockAutoLoadRef.current = false;
    setSuccess('');
    await loadSurveyById(id);
  };

  const publicLink = useMemo(() => {
    if (!publicToken) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/encuesta-bot/r/${publicToken}`;
  }, [publicToken]);

  const updateQuestion = useCallback((index, patch) => {
    setPreguntas((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  }, []);

  const addQuestion = () => {
    setPreguntas((prev) => [...prev, emptyQuestion(prev.length + 1)]);
  };

  const removeQuestion = (index) => {
    setPreguntas((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, orden: i + 1 }));
    });
  };

  const handleSave = async (nextEstado = estado) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = {
        id: isNewDraft ? undefined : (surveyId || undefined),
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        mensaje_gracias: mensajeGracias.trim(),
        estado: nextEstado,
        preguntas: preguntas.map((q, index) => ({
          orden: index + 1,
          tipo: q.tipo,
          titulo: q.titulo.trim(),
          requerida: q.requerida,
          opciones: q.tipo === 'opciones'
            ? (q.opciones || []).map((o) => String(o).trim()).filter(Boolean)
            : undefined
        }))
      };
      const json = await saveBotEncuesta(payload);
      const data = json?.data;
      setSurveyId(data?.id || null);
      setPublicToken(data?.public_token || '');
      setEstado(data?.estado || nextEstado);
      setSelectedListId(data?.id || null);
      setIsNewDraft(false);
      blockAutoLoadRef.current = false;
      await refreshSurveyList();
      setSuccess(nextEstado === 'publicada' ? 'Encuesta publicada correctamente' : 'Borrador guardado');
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="bot-encuesta-page bot-encuesta-page--crm">
        <div className="bot-encuesta-shell bot-encuesta-shell--loading">
          <div className="bot-encuesta-loader" aria-hidden />
          <p>Cargando generador…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bot-encuesta-page bot-encuesta-page--crm">
      <div className="bot-encuesta-shell">
        <header className="bot-encuesta-head">
          <div className="bot-encuesta-brand">
            <span className="bot-encuesta-brand-mark" aria-hidden>GSA</span>
            <div>
              <p className="bot-encuesta-eyebrow">Grupo Santa Ana</p>
              <h1 className="bot-encuesta-title">Generador de encuesta</h1>
              <p className="bot-encuesta-sub">
                Armá las preguntas que el bot enviará al finalizar el flujo.
              </p>
            </div>
          </div>
          <div className="bot-encuesta-head-actions">
            <span className={`bot-encuesta-status bot-encuesta-status--${estado}`}>
              {estado === 'publicada' ? 'Publicada' : 'Borrador'}
            </span>
            {canSeeReview ? (
              <Link to="/encuesta-bot/review" className="bot-encuesta-btn bot-encuesta-btn--ghost">
                Ver review →
              </Link>
            ) : null}
          </div>
        </header>

        {error ? <div className="bot-encuesta-alert bot-encuesta-alert--error" role="alert">{error}</div> : null}
        {success ? <div className="bot-encuesta-alert bot-encuesta-alert--ok" role="status">{success}</div> : null}

        <BotEncuestaSurveyList
          surveys={surveyList}
          selectedId={selectedListId}
          isNewDraft={isNewDraft}
          onSelect={handleSelectSurvey}
          onNew={startNewSurvey}
          showNewButton
          loading={listLoading}
        />

        <div className={`bot-encuesta-grid${switchingSurvey ? ' bot-encuesta-grid--busy' : ''}`} aria-busy={switchingSurvey}>
          <section className="bot-encuesta-panel">
            <h2 className="bot-encuesta-panel-title">Configuración general</h2>
            <label className="bot-encuesta-field">
              <span>Título</span>
              <input
                type="text"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Encuesta Bot Recetas"
              />
            </label>
            <label className="bot-encuesta-field">
              <span>Descripción</span>
              <textarea
                rows={2}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Breve intro para quien responde"
              />
            </label>
            <label className="bot-encuesta-field">
              <span>Mensaje de agradecimiento</span>
              <textarea
                rows={2}
                value={mensajeGracias}
                onChange={(e) => setMensajeGracias(e.target.value)}
                placeholder="¡Gracias por tu tiempo!"
              />
            </label>

            <div className="bot-encuesta-questions-head">
              <h2 className="bot-encuesta-panel-title">Preguntas</h2>
              <button type="button" className="bot-encuesta-btn bot-encuesta-btn--ghost" onClick={addQuestion}>
                + Agregar
              </button>
            </div>

            <div className="bot-encuesta-questions">
              {preguntas.map((q, index) => (
                <article key={`q-${index}`} className="bot-encuesta-question-card">
                  <div className="bot-encuesta-question-top">
                    <span className="bot-encuesta-question-index">{index + 1}</span>
                    <select
                      value={q.tipo}
                      onChange={(e) => updateQuestion(index, { tipo: e.target.value })}
                      aria-label={`Tipo pregunta ${index + 1}`}
                    >
                      {QUESTION_TYPES.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                    <label className="bot-encuesta-check">
                      <input
                        type="checkbox"
                        checked={q.requerida}
                        onChange={(e) => updateQuestion(index, { requerida: e.target.checked })}
                      />
                      Requerida
                    </label>
                    <button
                      type="button"
                      className="bot-encuesta-icon-btn"
                      onClick={() => removeQuestion(index)}
                      aria-label="Eliminar pregunta"
                      disabled={preguntas.length <= 1}
                    >
                      ×
                    </button>
                  </div>
                  <input
                    type="text"
                    className="bot-encuesta-question-input"
                    value={q.titulo}
                    onChange={(e) => updateQuestion(index, { titulo: e.target.value })}
                    placeholder="Escribí la pregunta…"
                  />
                  {q.tipo === 'opciones' ? (
                    <div className="bot-encuesta-options">
                      {(q.opciones || ['']).map((opt, optIndex) => (
                        <div key={`opt-${index}-${optIndex}`} className="bot-encuesta-option-row">
                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => {
                              const next = [...(q.opciones || [''])];
                              next[optIndex] = e.target.value;
                              updateQuestion(index, { opciones: next });
                            }}
                            placeholder={`Opción ${optIndex + 1}`}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        className="bot-encuesta-btn bot-encuesta-btn--ghost"
                        onClick={() => updateQuestion(index, { opciones: [...(q.opciones || []), ''] })}
                      >
                        + Opción
                      </button>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>

            <div className="bot-encuesta-actions">
              <button
                type="button"
                className="bot-encuesta-btn bot-encuesta-btn--secondary"
                disabled={saving}
                onClick={() => handleSave('borrador')}
              >
                Guardar borrador
              </button>
              <button
                type="button"
                className="bot-encuesta-btn bot-encuesta-btn--primary"
                disabled={saving}
                onClick={() => handleSave('publicada')}
              >
                {saving ? 'Guardando…' : 'Publicar encuesta'}
              </button>
            </div>
          </section>

          <aside className="bot-encuesta-preview">
            <h2 className="bot-encuesta-panel-title">Vista previa</h2>
            <div className="bot-encuesta-preview-card">
              <h3>{titulo || 'Sin título'}</h3>
              {descripcion ? <p>{descripcion}</p> : null}
              {preguntas.map((q, index) => (
                <div key={`prev-${index}`} className="bot-encuesta-preview-q">
                  <label>
                    {q.titulo || `Pregunta ${index + 1}`}
                    {q.requerida ? <span className="bot-encuesta-req">*</span> : null}
                  </label>
                  {q.tipo === 'rating' ? (
                    <div className="bot-encuesta-rating-preview">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span key={n} className="bot-encuesta-rating-dot">{n}</span>
                      ))}
                    </div>
                  ) : null}
                  {q.tipo === 'si_no' ? (
                    <div className="bot-encuesta-chip-row">
                      <span className="bot-encuesta-chip">Sí</span>
                      <span className="bot-encuesta-chip">No</span>
                    </div>
                  ) : null}
                  {q.tipo === 'texto' ? <div className="bot-encuesta-text-preview" /> : null}
                  {q.tipo === 'opciones' ? (
                    <div className="bot-encuesta-chip-col">
                      {(q.opciones || []).filter(Boolean).map((opt, i) => (
                        <span key={i} className="bot-encuesta-chip">{opt}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            {publicLink ? (
              <div className="bot-encuesta-link-box">
                <p className="bot-encuesta-link-label">Link público (para el bot)</p>
                {estado === 'publicada' ? (
                  <a
                    href={publicLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bot-encuesta-link-code bot-encuesta-link-code--clickable"
                  >
                    {publicLink}
                  </a>
                ) : (
                  <code className="bot-encuesta-link-code">{publicLink}</code>
                )}
                {estado === 'publicada' ? (
                  <a
                    href={publicLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bot-encuesta-link-open"
                  >
                    Abrir encuesta →
                  </a>
                ) : (
                  <p className="bot-encuesta-link-hint">Publicá la encuesta para activar el link.</p>
                )}
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}

const RATING_HINTS = {
  1: 'Muy difícil',
  2: 'Difícil',
  3: 'Regular',
  4: 'Fácil',
  5: 'Muy fácil'
};

function isAnswered(value) {
  return value !== undefined && value !== null && value !== '';
}

function BotEncuestaRespond() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const patientNombre = String(searchParams.get('nombre') || searchParams.get('name') || '').trim();
  const patientDni = String(searchParams.get('dni') || searchParams.get('documento') || '').trim();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [thanks, setThanks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const preguntas = survey?.preguntas || [];
  const requiredPreguntas = useMemo(
    () => preguntas.filter((q) => q.requerida !== false),
    [preguntas]
  );
  const requiredAnsweredCount = useMemo(
    () => requiredPreguntas.filter((q) => isAnswered(answers[q.id])).length,
    [requiredPreguntas, answers]
  );
  const canSubmit = useMemo(
    () => !requiredPreguntas.length || requiredPreguntas.every((q) => isAnswered(answers[q.id])),
    [requiredPreguntas, answers]
  );
  const progressPct = requiredPreguntas.length
    ? Math.round((requiredAnsweredCount / requiredPreguntas.length) * 100)
    : 0;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const json = await fetchBotEncuestaPublic(token);
        if (cancelled) return;
        setSurvey(json?.data || null);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Encuesta no disponible');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const setAnswer = (preguntaId, value) => {
    setAnswers((prev) => ({ ...prev, [preguntaId]: value }));
  };

  const toggleAnswer = (preguntaId, value) => {
    setAnswers((prev) => {
      if (prev[preguntaId] === value) {
        const next = { ...prev };
        delete next[preguntaId];
        return next;
      }
      return { ...prev, [preguntaId]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const preguntas = survey?.preguntas || [];
    for (const q of requiredPreguntas) {
      if (!isAnswered(answers[q.id])) {
        setError(`Respondé la pregunta: ${q.titulo}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        respuestas: preguntas.map((q) => ({
          pregunta_id: q.id,
          titulo: q.titulo,
          tipo: q.tipo,
          valor: answers[q.id]
        })),
        ...(patientNombre || patientDni ? {
          meta: {
            ...(patientNombre ? { nombre: patientNombre } : {}),
            ...(patientDni ? { dni: patientDni } : {})
          }
        } : {})
      };
      const json = await submitBotEncuestaResponse(token, payload);
      setThanks(json?.mensaje_gracias || survey?.mensaje_gracias || '¡Gracias!');
      setDone(true);
    } catch (err) {
      setError(err.message || 'No se pudo enviar');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bot-encuesta-page bot-encuesta-page--respond bot-encuesta-page--respond-premium">
        <div className="bot-encuesta-respond-ambient" aria-hidden>
          <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--a" />
          <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--b" />
        </div>
        <div className="bot-encuesta-respond-shell bot-encuesta-respond-shell--loading">
          <div className="bot-encuesta-respond-loader-card">
            <div className="bot-encuesta-loader" aria-hidden />
            <p>Preparando tu encuesta…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="bot-encuesta-page bot-encuesta-page--respond bot-encuesta-page--respond-premium">
        <div className="bot-encuesta-respond-ambient" aria-hidden>
          <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--a" />
          <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--b" />
        </div>
        <div className="bot-encuesta-respond-shell">
          <div className="bot-encuesta-respond-card bot-encuesta-respond-card--error">
            <span className="bot-encuesta-respond-error-icon" aria-hidden>!</span>
            <h1>Encuesta no disponible</h1>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="bot-encuesta-page bot-encuesta-page--respond bot-encuesta-page--respond-premium">
        <div className="bot-encuesta-respond-ambient" aria-hidden>
          <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--a" />
          <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--b" />
        </div>
        <div className="bot-encuesta-respond-shell">
          <div className="bot-encuesta-respond-card bot-encuesta-respond-card--done">
            <span className="bot-encuesta-done-ring" aria-hidden>
              <span className="bot-encuesta-done-icon">✓</span>
            </span>
            <p className="bot-encuesta-respond-done-eyebrow">Encuesta enviada</p>
            <h1>¡Gracias!</h1>
            <p className="bot-encuesta-respond-done-msg">{thanks}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bot-encuesta-page bot-encuesta-page--respond bot-encuesta-page--respond-premium">
      <div className="bot-encuesta-respond-ambient" aria-hidden>
        <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--a" />
        <span className="bot-encuesta-respond-orb bot-encuesta-respond-orb--b" />
      </div>

      <form className="bot-encuesta-respond-shell" onSubmit={handleSubmit}>
        <article className="bot-encuesta-respond-card">
          <header className="bot-encuesta-respond-hero">
            <div className="bot-encuesta-respond-brand">
              <span className="bot-encuesta-respond-mark" aria-hidden>GSA</span>
              <span className="bot-encuesta-respond-eyebrow">Grupo Santa Ana</span>
            </div>
            <h1 className="bot-encuesta-respond-title">{survey?.titulo}</h1>
            {survey?.descripcion ? (
              <p className="bot-encuesta-respond-lead">{survey.descripcion}</p>
            ) : null}
            {requiredPreguntas.length ? (
              <div className="bot-encuesta-respond-progress" aria-label={`${requiredAnsweredCount} de ${requiredPreguntas.length} obligatorias respondidas`}>
                <div className="bot-encuesta-respond-progress-meta">
                  <span>Tu progreso</span>
                  <strong>{requiredAnsweredCount}/{requiredPreguntas.length}</strong>
                </div>
                <div className="bot-encuesta-respond-progress-track">
                  <span className="bot-encuesta-respond-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            ) : null}
          </header>

          {error ? <div className="bot-encuesta-alert bot-encuesta-alert--error" role="alert">{error}</div> : null}

          <div className="bot-encuesta-respond-body">
            {preguntas.map((q, index) => (
              <section
                key={q.id}
                className={`bot-encuesta-respond-q-card${isAnswered(answers[q.id]) ? ' is-answered' : ''}`}
              >
                <div className="bot-encuesta-respond-q-head">
                  <span className="bot-encuesta-respond-q-num">{index + 1}</span>
                  <h2 className="bot-encuesta-respond-q-title">
                    {q.titulo}
                    {q.requerida ? <span className="bot-encuesta-req" aria-label="obligatoria">*</span> : null}
                  </h2>
                </div>

                {q.tipo === 'rating' ? (
                  <div className="bot-encuesta-rating-premium">
                    <div className="bot-encuesta-rating-premium-row">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`bot-encuesta-rating-premium-btn${answers[q.id] === n ? ' is-selected' : ''}`}
                          onClick={() => toggleAnswer(q.id, n)}
                          aria-pressed={answers[q.id] === n}
                          aria-label={`${n} — ${RATING_HINTS[n]}`}
                        >
                          <span className="bot-encuesta-rating-premium-value">{n}</span>
                          <span className="bot-encuesta-rating-premium-hint">{RATING_HINTS[n]}</span>
                        </button>
                      ))}
                    </div>
                    <div className="bot-encuesta-rating-premium-scale">
                      <span>1 = muy difícil</span>
                      <span>5 = muy fácil</span>
                    </div>
                  </div>
                ) : null}

                {q.tipo === 'si_no' ? (
                  <div className="bot-encuesta-yn-row">
                    {[
                      { value: 'Sí', icon: '✓', tone: 'yes' },
                      { value: 'No', icon: '✕', tone: 'no' }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`bot-encuesta-yn-btn bot-encuesta-yn-btn--${opt.tone}${answers[q.id] === opt.value ? ' is-selected' : ''}`}
                        onClick={() => toggleAnswer(q.id, opt.value)}
                        aria-pressed={answers[q.id] === opt.value}
                      >
                        <span className="bot-encuesta-yn-icon" aria-hidden>{opt.icon}</span>
                        <span>{opt.value}</span>
                      </button>
                    ))}
                  </div>
                ) : null}

                {q.tipo === 'texto' ? (
                  <div className="bot-encuesta-text-premium">
                    <textarea
                      rows={3}
                      value={answers[q.id] || ''}
                      onChange={(e) => setAnswer(q.id, e.target.value)}
                      placeholder="Contanos qué te gustó o qué mejorarías…"
                    />
                    {!q.requerida ? (
                      <span className="bot-encuesta-text-premium-hint">Opcional — podés dejarlo en blanco</span>
                    ) : null}
                  </div>
                ) : null}

                {q.tipo === 'opciones' ? (
                  <div className="bot-encuesta-options-premium">
                    {(q.opciones || []).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={`bot-encuesta-option-premium-btn${answers[q.id] === opt ? ' is-selected' : ''}`}
                        onClick={() => setAnswer(q.id, opt)}
                      >
                        <span className="bot-encuesta-option-premium-dot" aria-hidden />
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : null}
              </section>
            ))}
          </div>

          <footer className="bot-encuesta-respond-footer">
            <button
              type="submit"
              className="bot-encuesta-respond-submit"
              disabled={submitting || !canSubmit}
              aria-disabled={submitting || !canSubmit}
            >
              <span>{submitting ? 'Enviando…' : 'Enviar respuestas'}</span>
              {!submitting ? <span className="bot-encuesta-respond-submit-arrow" aria-hidden>→</span> : null}
            </button>
            <p className="bot-encuesta-respond-footnote">
              Tu opinión es anónima y nos ayuda a mejorar la experiencia.
            </p>
          </footer>
        </article>
      </form>
    </div>
  );
}

export default function BotEncuesta({ mode = 'builder' }) {
  if (mode === 'respond') return <BotEncuestaRespond />;
  return <BotEncuestaBuilder />;
}
